import { SearchUserEnriched } from 'lib-search-types';
import { User } from 'lib-shared-types-api';
import { useEffect, useMemo, useState } from 'react';

import { krakenApi } from 'apis/kraken';
import { AwayStatus } from 'apis/presence';
import { FavoritesPresenceListSection } from 'components/presence/components/favoritesPresenceListSection';
import { TeamPresenceListSections } from 'components/presence/components/teamPresenceListSections';
import { sortMembers } from 'components/presence/utils';
import {
  useMembersWithPresence,
  useOrganizationPresenceSubscription,
  usePresenceOverlaySync,
} from 'hooks/usePresenceCacheSync';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

export type TeamWithMembers = {
  id: string;
  name: string;
  members: SearchUserEnriched[];
};

type PresenceListSectionsProps = {
  profile: User;
  awayStatuses: AwayStatus[];
};

export const PresenceListSections = ({
  profile,
  awayStatuses,
}: PresenceListSectionsProps): JSX.Element => {
  useOrganizationPresenceSubscription();

  const { presenceSidebarShowSelf } = useFeatureFlag([
    'presence-sidebar-show-self',
  ]);

  const { favoriteAgentIds, addFavorite, removeFavorite } = useFavoriteAgents();

  // Frozen favorites state - prevents list jumping while dropdown is open
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [frozenFavoriteIds, setFrozenFavoriteIds] =
    useState<readonly string[]>(favoriteAgentIds);

  useEffect(() => {
    if (!isDropdownOpen) {
      setFrozenFavoriteIds(favoriteAgentIds);
    }
  }, [favoriteAgentIds, isDropdownOpen]);

  const { allMembers, teamIds, teamNames, membersLoading, membersFailed } =
    krakenApi.useReadSearchUsersEnrichedQuery(
      {
        scope: 'users',
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [{ value: 'member' }],
            },
          ],
        },
        page: 0,
        pageSize: 1000,
        enrichmentSpec: ['teams'],
      },
      {
        selectFromResult: ({ data, isLoading, isError }) => {
          const allMembers =
            data?.data.results.map(({ result }) => result) ?? [];
          const me = allMembers.find(({ id }) => id === profile.id);
          const activeTeams = me?.teams?.filter((t) => !t.is_deactivated) ?? [];

          return {
            allMembers,
            teamIds: activeTeams.map((t) => t.id),
            teamNames: activeTeams.reduce(
              (acc, t) => ({ ...acc, [t.id]: t.name }),
              {} as Record<string, string>,
            ),
            membersLoading: isLoading,
            membersFailed: isError,
          };
        },
      },
    );

  usePresenceOverlaySync();
  const membersWithPresence = useMembersWithPresence(allMembers);

  const favoriteMembers = useMemo(() => {
    const favorites = membersWithPresence.filter(
      (m) => m.id === profile.id || frozenFavoriteIds.includes(m.id),
    );
    const sorted = sortMembers(favorites);
    const me = sorted.find((m) => m.id === profile.id);
    const others = sorted.filter((m) => m.id !== profile.id);
    return me ? [me, ...others] : others;
  }, [membersWithPresence, profile.id, frozenFavoriteIds]);

  const teamsWithMembers: TeamWithMembers[] = useMemo(
    () =>
      teamIds
        .map((teamId, index) => ({
          id: teamId,
          name: teamNames[teamId] ?? `Team ${index + 1}`,
          members: sortMembers(
            membersWithPresence.filter(
              (member) =>
                member.teams?.some(
                  (t) => (typeof t === 'string' ? t : t.id) === teamId,
                ) &&
                (presenceSidebarShowSelf || member.id !== profile.id),
            ),
          ),
        }))
        .filter((team) => team.members.length > 0),
    [
      teamIds,
      teamNames,
      membersWithPresence,
      profile.id,
      presenceSidebarShowSelf,
    ],
  );

  const isLoading = membersLoading;
  const isError = membersFailed;

  return (
    <>
      <FavoritesPresenceListSection
        profile={profile}
        awayStatuses={awayStatuses}
        members={favoriteMembers}
        loading={isLoading}
        favoriteAgentIds={favoriteAgentIds}
        onAddToFavorites={addFavorite}
        onRemoveFromFavorites={removeFavorite}
        onDropdownOpenChange={setIsDropdownOpen}
      />
      <TeamPresenceListSections
        profile={profile}
        awayStatuses={awayStatuses}
        teams={teamsWithMembers}
        loading={isLoading}
        error={isError}
        favoriteAgentIds={favoriteAgentIds}
        onAddToFavorites={addFavorite}
        onRemoveFromFavorites={removeFavorite}
      />
    </>
  );
};
